/**
 * BowerNestLoader: the page- and section-level wait. The logo mark's feathers
 * lift off, weave into a turning nest, and settle back into the mark.
 *
 * WHERE IT GOES. A wait that OWNS a surface: a full-page or route-level
 * fallback, a detail page before its entity arrives, a preview pane, a settings
 * tab, a full-screen editor loading. Everywhere smaller keeps `BowerSpinner` (a
 * button, a row, a status line, a dialog), and content that has a known shape
 * keeps its skeleton, which is a better wait than any mark because it shows
 * what is coming.
 *
 * IT IS THE REAL MARK. Every shape is a piece of `assets/brand/logo-mark.svg`,
 * cut at the joins between feathers and moved rigidly, never redrawn. The
 * resting pose is the file itself, and the loop starts and ends there, so there
 * is no seam in the cycle. The geometry and the whole choreography (as CSS) are
 * generated by `scripts/generate-nest-loader.mjs`; this component only renders
 * them. The same run writes the identical mark into `index.html` as a
 * pre-JavaScript splash, so a slow network sees it on the first bytes.
 *
 * WHY CSS, NOT SMIL. The first version animated with SVG's own SMIL. Chromium
 * does not start SMIL timelines until the window `load` event, so on a slow
 * network a loader on screen before then sat frozen on the logo, which is the
 * one moment a loader exists for. CSS animations start the moment they are
 * painted. Each piece's transform is `rotate() translate() rotate() scale()`
 * in every keyframe, and CSS interpolates a matching function list function by
 * function, which is exactly the orbit. The comet tails animate a mask stop's
 * `stop-color`, which CSS can reach too.
 *
 * ONE PAGE CLOCK. Waits chain (route fallback, then workspace wait, then a
 * detail page's; a Suspense fallback, then an editor's own), each in the same
 * place. Every instance, and the splash, sets `--bnl-d` to minus the page
 * clock's position in the loop, as a negative animation delay, so a successor
 * carries on exactly where its predecessor was.
 *
 * PAINT. `spinner.gradientStop1..3`: bluebird, twilight and an end stop
 * (Sunset in both modes: 500 in light, 400 in dark), the stops `BowerSpinner`
 * uses. In light mode the Sunset head is under WCAG 1.4.11's 3:1, a recorded
 * trade for the brand colour (see `spinnerContrast.test.ts`). The gradient is
 * laid over the mark exactly as `icon.gradient`'s 135deg is, and each piece
 * carries it as it moves. The trailing fallback in
 * the paint value is load-bearing: an unresolved `url(#…)` paints nothing.
 *
 * THREE ENVIRONMENTS, as in `BowerSpinner`:
 *
 *   reduced-motion  a CSS rule removes every animation and gives each piece
 *                   a static transform equal to its first keyframe: the still
 *                   mark. Removed, not paused, so switching the setting on
 *                   mid-orbit lands on the mark rather than freezing
 *                   wherever the piece was; !important, because it must beat
 *                   the more specific per-piece rules.
 *   forced-colors   the paint server may not resolve; fill `currentColor`.
 *   print           paint servers are commonly dropped; flat mid stop.
 */

import { Box, type BoxProps } from '@chakra-ui/react';
import { type CSSProperties, Fragment, useId, useState } from 'react';

import { SPINNER_FLAT_FALLBACK } from '../../theme/iconGradient';
import {
  NEST_CENTER,
  NEST_CSS,
  NEST_GRADIENT,
  NEST_LOGO_PATHS,
  NEST_LOOP_SECONDS,
  NEST_PIECES,
  NEST_VIEWBOX,
} from './nestLoaderGeometry';

/** `md` (4rem) for a pane or tab, `lg` (6rem) for a page. */
const SIZES = { md: '4rem', lg: '6rem' } as const;

export type BowerNestLoaderSize = keyof typeof SIZES;

export interface BowerNestLoaderProps extends Omit<BoxProps, 'children'> {
  /** @default 'lg' */
  size?: BowerNestLoaderSize;
  /**
   * What a screen reader announces. `null` when visible text beside the loader
   * already says what is happening. Same contract as `BowerSpinner`.
   *
   * @default 'Loading'
   */
  label?: string | null;
}

/**
 * Minus the page clock's position in the loop, as a CSS time. The clock is
 * `performance.now()`, whose zero is navigation start: the splash in
 * `index.html` reads the same clock, so it hands over to the first React
 * loader without a jump, and so does every later handover.
 */
function pageClockDelay(): string {
  return `${-((performance.now() / 1000) % NEST_LOOP_SECONDS)}s`;
}

const [CX, CY] = NEST_CENTER;
const [GX1, GY1, GX2, GY2] = NEST_GRADIENT;

export function BowerNestLoader({ size = 'lg', label = 'Loading', ...rest }: BowerNestLoaderProps) {
  // `useId` returns colons, which a `url(#…)` reference does not survive.
  const uid = `bower-nest-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  // Read once per mount: the animations start at mount, so this is their phase.
  const [delay] = useState(pageClockDelay);
  const gradientId = `${uid}-g`;
  const decorative = label === null;

  return (
    <>
      {/* Hoisted to <head> and deduplicated by React across every instance. */}
      <style href="bower-nest-loader" precedence="default">
        {NEST_CSS}
      </style>
      <Box
        as="svg"
        className="bnl"
        viewBox={NEST_VIEWBOX}
        width={SIZES[size]}
        height={SIZES[size]}
        flexShrink={0}
        display="block"
        // `img`, not `status`, for the reason `BowerSpinner` gives: the
        // container that changed owns the announcement.
        role={decorative ? undefined : 'img'}
        aria-label={decorative ? undefined : label}
        aria-hidden={decorative ? true : undefined}
        focusable="false"
        data-testid="bower-nest-loader"
        style={{ '--bnl-d': delay } as CSSProperties}
        sx={{
          fill: `url(#${gradientId}) currentColor`,
          '@media (forced-colors: active)': { fill: 'currentColor' },
          '@media print': { fill: SPINNER_FLAT_FALLBACK },
        }}
        {...rest}
      >
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1={GX1}
            y1={GY1}
            x2={GX2}
            y2={GY2}
          >
            <stop offset="0" stopColor="var(--chakra-colors-spinner-gradientStop1)" />
            <stop offset="0.45" stopColor="var(--chakra-colors-spinner-gradientStop2)" />
            <stop offset="1" stopColor="var(--chakra-colors-spinner-gradientStop3)" />
          </linearGradient>
          {NEST_PIECES.map((piece, i) =>
            piece.tail ? (
              <Fragment key={i}>
                <linearGradient
                  id={`${uid}-t${i}`}
                  gradientUnits="userSpaceOnUse"
                  x1={piece.tail[0]}
                  y1={piece.tail[1]}
                  x2={piece.tail[2]}
                  y2={piece.tail[3]}
                >
                  {/* Luminance for the mask, not a colour anyone sees: white
                      keeps the feather, and class t<i> fades its base to black. */}
                  <stop className={`t${i}`} offset="0.05" stopColor="white" />
                  <stop offset="0.7" stopColor="white" />
                </linearGradient>
                <mask
                  id={`${uid}-m${i}`}
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="220"
                  height="180"
                >
                  <rect width="220" height="180" fill={`url(#${uid}-t${i})`} />
                </mask>
              </Fragment>
            ) : null,
          )}
        </defs>
        <g transform={`translate(${CX} ${CY})`}>
          <g className="sp">
            {NEST_PIECES.map((piece, i) => (
              <g key={i} className={`p${i}`}>
                <path
                  transform={`translate(${-piece.anchor[0]} ${-piece.anchor[1]})`}
                  mask={piece.tail ? `url(#${uid}-m${i})` : undefined}
                  className={piece.tail ? undefined : 'pf'}
                  d={piece.d}
                />
              </g>
            ))}
            <g className="ov">
              <g transform={`translate(${-CX} ${-CY})`}>
                {NEST_LOGO_PATHS.map((d) => (
                  <path key={d.slice(0, 24)} d={d} />
                ))}
              </g>
            </g>
          </g>
        </g>
      </Box>
    </>
  );
}
